import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Users, MessageCircle, Heart, Send } from "lucide-react";
import { BADGE_COLORS } from "./CommunityNav";

const formatDate = (dateString) =>
  new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

export const FeedTab = ({
  user, posts, loading, newPostContent, posting, communityUsers,
  onPostContentChange, onCreatePost, onLikePost, onViewComments, onSelectUser
}) => {
  const { t } = useTranslation();

  const renderPosts = () => {
    if (loading) {
      return (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }
    if (posts.length === 0) {
      return (
        <Card className="bg-card border-border">
          <CardContent className="py-12 text-center">
            <MessageCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">{t('community.noPosts')}</p>
          </CardContent>
        </Card>
      );
    }
    return (
      <div className="space-y-4">
        {posts.map((post) => (
          <Card key={post.post_id} className="bg-card border-border" data-testid={`post-${post.post_id}`}>
            <CardContent className="p-4">
              <div className="flex items-start gap-3 mb-4">
                <Avatar className="w-10 h-10">
                  <AvatarImage src={post.user_picture} />
                  <AvatarFallback className="bg-primary/20 text-primary">
                    {post.user_name?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <p className="font-semibold">{post.user_name}</p>
                  <p className="text-xs text-muted-foreground">{formatDate(post.created_at)}</p>
                </div>
              </div>
              <p className="text-sm mb-4 whitespace-pre-wrap">{post.content}</p>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => onLikePost(post.post_id)}
                  className={`flex items-center gap-1 text-sm ${post.likes?.includes(user?.user_id) ? 'text-red-500' : 'text-muted-foreground hover:text-red-500'}`}
                >
                  <Heart className={`w-4 h-4 ${post.likes?.includes(user?.user_id) ? 'fill-current' : ''}`} />
                  {post.likes?.length || 0}
                </button>
                <button
                  onClick={() => onViewComments(post)}
                  className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary"
                >
                  <MessageCircle className="w-4 h-4" />
                  {post.comment_count || 0}
                </button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <Card className="bg-card border-border" data-testid="create-post-card">
          <CardContent className="p-4">
            <Textarea
              placeholder={t('community.postPlaceholder')}
              value={newPostContent}
              onChange={(e) => onPostContentChange(e.target.value)}
              className="bg-transparent border-border focus:border-primary min-h-[100px] mb-4"
              data-testid="post-content-input"
            />
            <div className="flex justify-end">
              <Button
                onClick={onCreatePost}
                disabled={posting || !newPostContent.trim()}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="submit-post-button"
              >
                <Send className="w-4 h-4 mr-2" />
                {posting ? t('common.loading') : t('community.post')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {renderPosts()}
      </div>

      <div>
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              {t('community.members')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {communityUsers.slice(0, 5).map((member) => (
              <div
                key={member.user_id}
                className="flex items-center gap-3 py-2 cursor-pointer hover:bg-muted/20 rounded px-2 -mx-2"
                onClick={() => onSelectUser(member)}
              >
                <Avatar className="w-8 h-8">
                  <AvatarImage src={member.picture} />
                  <AvatarFallback className="bg-primary/20 text-primary text-xs">
                    {member.name?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{member.name}</p>
                  <p className={`text-xs ${BADGE_COLORS[member.badge_tier]}`}>{member.badge_tier}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const MembersTab = ({ communityUsers, onSelectUser }) => {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {communityUsers.map((member) => (
        <Card key={member.user_id} className="bg-card border-border card-hover">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Avatar className="w-12 h-12">
                <AvatarImage src={member.picture} />
                <AvatarFallback className="bg-primary/20 text-primary">
                  {member.name?.[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <p className="font-semibold">{member.name}</p>
                <p className={`text-sm ${BADGE_COLORS[member.badge_tier]}`}>{member.badge_tier}</p>
                <p className="text-xs text-muted-foreground">{member.total_stars} {t('leaderboard.stars')}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onSelectUser(member)}
                className="border-border hover:border-primary"
              >
                <MessageCircle className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export const MessagesTab = ({ user, messages }) => {
  const { t } = useTranslation();

  const conversations = (() => {
    const convMap = new Map();
    messages.forEach((msg) => {
      const otherUserId = msg.sender_id === user?.user_id ? msg.receiver_id : msg.sender_id;
      const otherUserName = msg.sender_id === user?.user_id ? "You" : msg.sender_name;
      if (!convMap.has(otherUserId)) {
        convMap.set(otherUserId, { userId: otherUserId, userName: otherUserName, messages: [] });
      }
      convMap.get(otherUserId).messages.push(msg);
    });
    return Array.from(convMap.values());
  })();

  return (
    <Card className="bg-card border-border">
      <CardContent className="p-4">
        {messages.length === 0 ? (
          <div className="text-center py-12">
            <MessageCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">{t('community.noMessages')}</p>
            <p className="text-sm text-muted-foreground mt-2">{t('community.sendMessage')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {conversations.map((conv) => (
              <div key={conv.userId} className="border-b border-border pb-4 last:border-0">
                <p className="font-semibold mb-2">{conv.userName}</p>
                {conv.messages.slice(0, 3).map((msg) => (
                  <div
                    key={msg.message_id}
                    className={`p-3 rounded mb-2 ${msg.sender_id === user?.user_id ? 'bg-primary/10 ml-8' : 'bg-muted/20 mr-8'}`}
                  >
                    <p className="text-sm">{msg.content}</p>
                    <p className="text-xs text-muted-foreground mt-1">{formatDate(msg.created_at)}</p>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
